import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CheckSquare, ArrowRight } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { format } from "date-fns";
import DeadlineNotifications from "@/components/projects/DeadlineNotifications";

const priorityColors = {
  low: "bg-slate-100 text-slate-700",
  medium: "bg-blue-100 text-blue-700",
  high: "bg-amber-100 text-amber-700",
  urgent: "bg-rose-100 text-rose-700",
};

export default function MyTasksWidget() {
  const { data: user } = useQuery({
    queryKey: ["currentUser"],
    queryFn: () => base44.auth.me(),
  });

  const { data: tasks = [], isLoading } = useQuery({
    queryKey: ["myTasks", user?.email],
    queryFn: () => base44.entities.Task.filter({ assigned_to: user?.email, status: ["todo", "in_progress"] }, "-created_date"),
    enabled: !!user?.email,
  });

  if (isLoading) {
    return (
      <Card className="p-6 bg-white border-0 shadow-sm">
        <div className="animate-pulse space-y-3">
          <div className="h-4 bg-slate-200 rounded w-1/3" />
          <div className="h-16 bg-slate-100 rounded" />
          <div className="h-16 bg-slate-100 rounded" />
        </div>
      </Card>
    );
  }

  return (
    <>
      <DeadlineNotifications />
      <div className="mb-6" />
      <Card className="p-6 bg-white border-0 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <CheckSquare className="w-5 h-5 text-zinc-900" />
            <h3 className="font-semibold text-slate-900">Mine Oppgaver</h3>
          </div>
          <Link to={createPageUrl("Tasks")}>
            <Button variant="ghost" size="sm">
              <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
        </div>
      
      {tasks.length === 0 ? (
        <p className="text-sm text-slate-500 text-center py-4">Ingen aktive oppgaver</p>
      ) : (
        <div className="space-y-2">
          {tasks.slice(0, 5).map(task => (
            <div key={task.id} className="p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-slate-900 text-sm truncate">{task.title}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge className={`text-xs border-0 ${priorityColors[task.priority]}`}>
                      {task.priority}
                    </Badge>
                    {task.due_date && (
                      <span className="text-xs text-slate-500">
                        {format(new Date(task.due_date), "dd.MM.yyyy")}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      </Card>
    </>
  );
}